"use client";

import { useState } from "react";
import { resendOrder, type AccountOrder } from "./account-api";
import { formatAccountAmount, formatAccountDate } from "./account-format";

type DeliveryStage = NonNullable<AccountOrder["delivery_stage"]>;

const DELIVERY_STEPS: ReadonlyArray<{ stage: DeliveryStage; label: string }> = [
  { stage: "paid", label: "Payment confirmed" },
  { stage: "preparing", label: "Preparing your reading" },
  { stage: "ready", label: "Ready to open" },
  { stage: "delivered", label: "Sent to you" },
];

const STAGE_INDEX: Record<DeliveryStage, number> = {
  paid: 0,
  preparing: 1,
  ready: 2,
  delivered: 3,
};

export function deliveryStageFor(order: AccountOrder): DeliveryStage {
  if (order.delivery_stage) return order.delivery_stage;
  if (order.delivered) return "delivered";
  if (order.generated_at) return "ready";
  return "preparing";
}

export function AccountReportDelivery({ order, token }: { order: AccountOrder; token: string }) {
  const stage = deliveryStageFor(order);
  const amount = formatAccountAmount(order);

  return (
    <article className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-surface-raised)] p-5 shadow-[var(--shadow-elevation-low)]">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.08em] text-[var(--color-accent)]">
            Report delivery
          </p>
          <h3 className="mt-1 font-serif text-xl">{order.title}</h3>
          <p className="mt-1 text-sm text-[var(--color-text-sub)]">
            Purchased {formatAccountDate(order.purchased_at)}
            {amount ? ` · ${amount}` : ""}
          </p>
        </div>
        <DeliveryBadge stage={stage} />
      </div>

      <ol
        aria-label={`${order.title} delivery progress`}
        className="mt-5 grid gap-3 sm:grid-cols-4"
      >
        {DELIVERY_STEPS.map((step, index) => {
          const complete = index < STAGE_INDEX[stage];
          const current = index === STAGE_INDEX[stage];
          return (
            <li
              key={step.stage}
              aria-current={current ? "step" : undefined}
              className="flex items-center gap-3 sm:block"
            >
              <span
                aria-hidden="true"
                className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full border text-xs font-bold sm:mb-2 ${
                  complete || current
                    ? "border-[var(--color-accent)] bg-[var(--color-accent)] text-white"
                    : "border-[var(--color-border)] text-[var(--color-text-faint)]"
                }`}
              >
                {index + 1}
              </span>
              <span
                className={`text-xs leading-5 ${
                  complete || current
                    ? "font-semibold text-[var(--color-text-main)]"
                    : "text-[var(--color-text-faint)]"
                }`}
              >
                {step.label}
              </span>
            </li>
          );
        })}
      </ol>

      <div className="mt-5 border-t border-[var(--color-border-light)] pt-4">
        <p role="status" className="text-sm leading-6 text-[var(--color-text-sub)]">
          {deliveryMessage(order, stage)}
        </p>
        <div className="mt-4 flex flex-wrap items-start gap-3">
          {stage === "ready" || stage === "delivered" ? (
            order.download_url ? (
              <a
                className="inline-flex min-h-11 items-center justify-center rounded-xl bg-[var(--color-accent)] px-5 py-3 text-sm font-semibold text-white"
                href={order.download_url}
              >
                Read report
              </a>
            ) : null
          ) : null}
          {order.can_resend ? (
            <ResendReportControl
              order={order}
              token={token}
              emphasized={stage === "paid" || stage === "preparing"}
            />
          ) : null}
        </div>
      </div>
    </article>
  );
}

function DeliveryBadge({ stage }: { stage: DeliveryStage }) {
  const label = {
    paid: "Payment received",
    preparing: "In preparation",
    ready: "Ready here",
    delivered: "Delivered",
  }[stage];
  return (
    <span className="inline-flex self-start rounded-full border border-[var(--color-accent-border)] bg-[var(--color-accent-light)] px-3 py-1 text-xs font-semibold text-[var(--color-accent)]">
      {label}
    </span>
  );
}

function deliveryMessage(order: AccountOrder, stage: DeliveryStage): string {
  if (stage === "paid") {
    return "Your payment is confirmed. Report preparation will begin shortly.";
  }
  if (stage === "preparing") {
    return "Your report is being prepared. You can leave this page; delivery will continue in the background.";
  }
  if (stage === "ready") {
    const generated = order.generated_at ? ` since ${formatAccountDate(order.generated_at)}` : "";
    return `Your private reading is ready here${generated}. Email or WhatsApp delivery may still be on the way.`;
  }
  if (order.delivery_channel === "whatsapp") {
    return `Sent to your WhatsApp${deliveryDate(order)}. Your private copy also stays here.`;
  }
  if (order.delivery_channel === "email") {
    return `Sent to your email${deliveryDate(order)}. Your private copy also stays here.`;
  }
  if (order.delivery_channel === "download") {
    return "Opened from your private report link. Your copy remains available here.";
  }
  return "Delivery is confirmed. Your private copy remains available here.";
}

function deliveryDate(order: AccountOrder): string {
  return order.delivered_at ? ` on ${formatAccountDate(order.delivered_at)}` : "";
}

function ResendReportControl({
  order,
  token,
  emphasized,
}: {
  order: AccountOrder;
  token: string;
  emphasized: boolean;
}) {
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState("");
  const [error, setError] = useState("");

  async function handleResend() {
    if (busy) return;
    setBusy(true);
    setError("");
    setDone("");
    try {
      const result = await resendOrder(token, order.order_id);
      if (result.ok) setDone(result.message || "Your report is on its way.");
      else setError(result.message || "We could not send it yet. Please try again shortly.");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not send your report");
    }
    setBusy(false);
  }

  if (done) return <p className="text-sm font-semibold text-[var(--color-success)]">{done}</p>;

  return (
    <div>
      <button
        type="button"
        disabled={busy}
        onClick={handleResend}
        className={`inline-flex min-h-11 items-center justify-center rounded-xl px-5 py-3 text-sm font-semibold disabled:cursor-not-allowed disabled:opacity-60 ${
          emphasized
            ? "bg-[var(--color-accent)] text-white"
            : "border border-[var(--color-accent)] text-[var(--color-accent)]"
        }`}
      >
        {busy ? "Checking delivery..." : emphasized ? "Check and send report" : "Send again"}
      </button>
      {error ? <p className="mt-2 max-w-md text-sm text-[var(--color-error)]">{error}</p> : null}
    </div>
  );
}
